// SPDX-License-Identifier: Apache-2.0

// forceLayoutStep — TypeScript dispatch wrapper for the N-body force sim.
//
// Three dispatches per frame, all on a single command encoder:
//   1. accumulate_repel  (forceLayoutStep.wgsl entry "accumulate_repel")
//   2. springs main      (forceLayoutSprings.wgsl entry "main")  [skipped if 0 edges]
//   3. integrate         (forceLayoutStep.wgsl entry "integrate")
//
// Output buffer `pos_out` is INTENDED to be bound directly to a three.js
// InstancedMesh transform attribute via TSL `storage()`, so no CPU readback per
// frame. See ../../apps/web/src/routes/Orbit.tsx for the binding choice we shipped.
//
// Diagnostics baked in (per kernels/CLAUDE.md discipline):
//   - module.getCompilationInfo() after createShaderModule, throw on errors.
//   - device.pushErrorScope("validation") around encoder + submit, throw on
//     non-null error after first step.
//   - first-step pos_out sample-readback to verify non-zero / non-NaN.

import stepShader from "./forceLayoutStep.wgsl?raw";
import springsShader from "./forceLayoutSprings.wgsl?raw";

// CPU constants from vault-orbit.tsx:164–173. Port byte-for-byte.
export const FORCE_CONSTANTS = {
  K_REPEL: 0.9,
  MIN_D2: 0.35,
  K_SPRING: 0.06,
  K_SPRING_SEM: 0.03,
  REST: 2.2,
  REST_SEM: 2.8,
  CENTER_K: 0.03,
  DAMPING: 0.86,
  MAX_V: 6.0,
  CAP_DT_MAX: 0.05,
  /** q16.16 fixed-point scale. Springs use atomicAdd<i32>; repel/integrate use f32 ↔ i32 cast. */
  FP_SCALE: 65536.0,
} as const;

export type ForceEdgeKind = 0 | 1; // 0 = explicit, 1 = semantic

/** Edge tuple: (a, b, kind). For semantic edges (kind=1) the w (cosine sim 0..1) goes in `w`. */
export interface ForceEdge {
  a: number;
  b: number;
  kind: ForceEdgeKind;
  w?: number; // semantic weight; 1.0 for explicit
}

export interface ForceLayoutSetup {
  device: GPUDevice;

  // Pipelines.
  pipelineRepel: GPUComputePipeline;
  pipelineIntegrate: GPUComputePipeline;
  pipelineSprings: GPUComputePipeline | null;

  // Bind groups (built once, reused every frame).
  stepBindGroup: GPUBindGroup;
  springsBindGroup: GPUBindGroup | null;

  // Buffers.
  paramsBuf: GPUBuffer;
  springsParamsBuf: GPUBuffer;
  posBuf: GPUBuffer; // pos[N] vec3<f32> — input each frame (we copy pos_out → pos before next step)
  posOutBuf: GPUBuffer; // pos_out[N] vec3<f32> — output (the buffer three.js binds to)
  velBuf: GPUBuffer; // vel[N] vec3<f32>
  forceFixedBuf: GPUBuffer; // 4 × i32 per node, q16.16 (atomic in springs kernel)
  edgesBuf: GPUBuffer | null;
  /** Small staging buffer used for first-step diag readback only. Pre-mappable. */
  diagReadbackBuf: GPUBuffer;

  n: number;
  nEdges: number;
  /** Padded count of vec3<f32> slots — vec3 is 16-byte-aligned in std430 so we allocate as vec4. */
  posStride: number;
  /** True until we've validated the first step output. */
  firstStep: boolean;
}

const TILE = 256;
const SPRING_WG = 64;

// vec3<f32> in WGSL std430 occupies 16 bytes (alignment 16). We allocate per-node
// stride = 16 bytes for pos/vel buffers, and 16 bytes (4 i32) for force_fixed.
const NODE_BYTES = 16;
const FORCE_BYTES = 16; // 4 × i32

async function compileModule(device: GPUDevice, code: string, label: string): Promise<GPUShaderModule> {
  const module = device.createShaderModule({ code, label });
  const info = await module.getCompilationInfo();
  const errors = info.messages.filter((m) => m.type === "error");
  if (errors.length > 0) {
    const text = errors.map((e) => `  L${e.lineNum}:${e.linePos} ${e.message}`).join("\n");
    throw new Error(`${label} WGSL compile errors:\n${text}`);
  }
  const warnings = info.messages.filter((m) => m.type === "warning");
  if (warnings.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[${label} shader] compile warnings:`, warnings);
  }
  return module;
}

/**
 * Build pipelines + buffers + bind groups for an N-node, E-edge force sim.
 * `initialPositions` is Float32Array of length N*3 (xyz) or N*4 (xyz + pad). Either is accepted.
 * `edges` is a flat list; pass `[]` for N-body-only (no springs).
 */
export async function setupForceLayout(
  device: GPUDevice,
  n: number,
  edges: ForceEdge[],
  initialPositions: Float32Array,
): Promise<ForceLayoutSetup> {
  if (n <= 0) throw new Error("n must be > 0");
  const posStride = 4; // vec3 padded to vec4 in std430

  const stepModule = await compileModule(device, stepShader, "forceLayoutStep");

  const stepBgl = device.createBindGroupLayout({
    label: "forceLayout-step-bgl",
    entries: [
      { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
      { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // pos
      { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },           // vel
      { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },           // pos_out
      { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },           // force_fixed (atomic in WGSL, plain storage in BGL)
    ],
  });
  const stepLayout = device.createPipelineLayout({ bindGroupLayouts: [stepBgl] });

  const pipelineRepel = device.createComputePipeline({
    label: "forceLayout-repel",
    layout: stepLayout,
    compute: { module: stepModule, entryPoint: "accumulate_repel" },
  });
  const pipelineIntegrate = device.createComputePipeline({
    label: "forceLayout-integrate",
    layout: stepLayout,
    compute: { module: stepModule, entryPoint: "integrate" },
  });

  // Buffers.
  const paramsBuf = device.createBuffer({
    label: "forceLayout-params",
    size: 32, // 8 × 4B fields, aligned to 16
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
  const posBuf = device.createBuffer({
    label: "forceLayout-pos",
    size: n * NODE_BYTES,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
  });
  const posOutBuf = device.createBuffer({
    label: "forceLayout-pos-out",
    // Mark COPY_SRC so three.js (or our tiny copy-out path) can read back; mark VERTEX
    // so it's bindable as an InstancedBufferAttribute via TSL storage() in r171+.
    size: n * NODE_BYTES,
    usage:
      GPUBufferUsage.STORAGE |
      GPUBufferUsage.COPY_SRC |
      GPUBufferUsage.COPY_DST |
      GPUBufferUsage.VERTEX,
  });
  const velBuf = device.createBuffer({
    label: "forceLayout-vel",
    size: n * NODE_BYTES,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    mappedAtCreation: true,
  });
  // Init velocities to zero.
  new Float32Array(velBuf.getMappedRange()).fill(0);
  velBuf.unmap();

  const forceFixedBuf = device.createBuffer({
    label: "forceLayout-force-fixed",
    size: n * FORCE_BYTES,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });

  const diagReadbackBuf = device.createBuffer({
    label: "forceLayout-diag-readback",
    // Sample first 32 nodes (16 B each) on first step to validate output.
    size: Math.min(n, 32) * NODE_BYTES,
    usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
  });

  // Pack initialPositions into the vec4-padded buffer.
  {
    const padded = new Float32Array(n * posStride);
    if (initialPositions.length === n * 3) {
      for (let i = 0; i < n; i++) {
        padded[i * 4 + 0] = initialPositions[i * 3 + 0]!;
        padded[i * 4 + 1] = initialPositions[i * 3 + 1]!;
        padded[i * 4 + 2] = initialPositions[i * 3 + 2]!;
        padded[i * 4 + 3] = 0;
      }
    } else if (initialPositions.length === n * 4) {
      padded.set(initialPositions);
    } else {
      throw new Error(`initialPositions length must be N*3 or N*4 (N=${n})`);
    }
    device.queue.writeBuffer(posBuf, 0, padded.buffer, padded.byteOffset, padded.byteLength);
    // pos_out starts as a copy of pos so first frame has something sensible to render
    // before integrate runs.
    device.queue.writeBuffer(posOutBuf, 0, padded.buffer, padded.byteOffset, padded.byteLength);
  }

  // Springs setup (if any).
  let pipelineSprings: GPUComputePipeline | null = null;
  let springsBindGroup: GPUBindGroup | null = null;
  let edgesBuf: GPUBuffer | null = null;
  const springsParamsBuf = device.createBuffer({
    label: "forceLayout-springs-params",
    size: 32,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  if (edges.length > 0) {
    const springsModule = await compileModule(device, springsShader, "forceLayoutSprings");
    const springsBgl = device.createBindGroupLayout({
      label: "forceLayout-springs-bgl",
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // pos
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } }, // edges
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },           // force_fixed
      ],
    });
    const springsLayout = device.createPipelineLayout({ bindGroupLayouts: [springsBgl] });
    pipelineSprings = device.createComputePipeline({
      label: "forceLayout-springs",
      layout: springsLayout,
      compute: { module: springsModule, entryPoint: "main" },
    });

    // Pack edges as 4×u32 per edge.
    const edgeBytes = edges.length * 16;
    edgesBuf = device.createBuffer({
      label: "forceLayout-edges",
      size: edgeBytes,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    });
    {
      // Single getMappedRange() call — WebGPU forbids overlapping ranges. We then
      // create two typed-array views over the same ArrayBuffer.
      const range = edgesBuf.getMappedRange();
      const u32 = new Uint32Array(range);
      const f32 = new Float32Array(range);
      for (let i = 0; i < edges.length; i++) {
        const e = edges[i]!;
        u32[i * 4 + 0] = e.a >>> 0;
        u32[i * 4 + 1] = e.b >>> 0;
        u32[i * 4 + 2] = e.kind >>> 0;
        // Bit-pack the f32 weight into the 4th u32 slot via Float32Array view.
        f32[i * 4 + 3] = e.kind === 1 ? (e.w ?? 1.0) : 1.0;
      }
      edgesBuf.unmap();
    }

    // springs uniform.
    {
      const springsParams = new ArrayBuffer(32);
      const u32 = new Uint32Array(springsParams);
      const f32 = new Float32Array(springsParams);
      u32[0] = edges.length >>> 0;
      f32[1] = FORCE_CONSTANTS.K_SPRING;
      f32[2] = FORCE_CONSTANTS.K_SPRING_SEM;
      f32[3] = FORCE_CONSTANTS.REST;
      f32[4] = FORCE_CONSTANTS.REST_SEM;
      f32[5] = FORCE_CONSTANTS.FP_SCALE;
      // _pad0, _pad1 left zero
      device.queue.writeBuffer(springsParamsBuf, 0, springsParams);
    }

    springsBindGroup = device.createBindGroup({
      label: "forceLayout-springs-bg",
      layout: springsBgl,
      entries: [
        { binding: 0, resource: { buffer: springsParamsBuf } },
        { binding: 1, resource: { buffer: posBuf } },
        { binding: 2, resource: { buffer: edgesBuf } },
        { binding: 3, resource: { buffer: forceFixedBuf } },
      ],
    });
  }

  const stepBindGroup = device.createBindGroup({
    label: "forceLayout-step-bg",
    layout: stepBgl,
    entries: [
      { binding: 0, resource: { buffer: paramsBuf } },
      { binding: 1, resource: { buffer: posBuf } },
      { binding: 2, resource: { buffer: velBuf } },
      { binding: 3, resource: { buffer: posOutBuf } },
      { binding: 4, resource: { buffer: forceFixedBuf } },
    ],
  });

  return {
    device,
    pipelineRepel,
    pipelineIntegrate,
    pipelineSprings,
    stepBindGroup,
    springsBindGroup,
    paramsBuf,
    springsParamsBuf,
    posBuf,
    posOutBuf,
    velBuf,
    forceFixedBuf,
    edgesBuf,
    diagReadbackBuf,
    n,
    nEdges: edges.length,
    posStride,
    firstStep: true,
  };
}

/**
 * Run one force-sim step. `dt` is in seconds; capped at CAP_DT_MAX (0.05) per CPU contract.
 *
 * After this returns, `setup.posOutBuf` holds the new positions. We then issue a
 * pos_out → pos copy on the same encoder so the next call uses the freshly integrated
 * positions as input. (Two-buffer ping-pong without a swap, since pos_out is also
 * what three.js renders from — keep it stable for the renderer.)
 *
 * Optional `commandEncoder` lets the caller fold this dispatch into a larger encoder
 * (e.g. shared with three.js's render encoder). If omitted, we create + submit our own.
 */
export async function stepForceLayout(
  setup: ForceLayoutSetup,
  dt: number,
  commandEncoder?: GPUCommandEncoder,
): Promise<void> {
  const { device, n, nEdges } = setup;
  const cap_dt = Math.min(dt, FORCE_CONSTANTS.CAP_DT_MAX);

  // Write params uniform (n + 7×f32). Layout matches WGSL `Params` struct.
  {
    const buf = new ArrayBuffer(32);
    const u32 = new Uint32Array(buf);
    const f32 = new Float32Array(buf);
    u32[0] = n >>> 0;
    f32[1] = FORCE_CONSTANTS.K_REPEL;
    f32[2] = FORCE_CONSTANTS.MIN_D2;
    f32[3] = FORCE_CONSTANTS.CENTER_K;
    f32[4] = FORCE_CONSTANTS.DAMPING;
    f32[5] = FORCE_CONSTANTS.MAX_V;
    f32[6] = cap_dt;
    f32[7] = FORCE_CONSTANTS.FP_SCALE;
    device.queue.writeBuffer(setup.paramsBuf, 0, buf);
  }

  const ownEncoder = commandEncoder == null;
  const enc = commandEncoder ?? device.createCommandEncoder({ label: "forceLayoutStep" });

  if (ownEncoder) device.pushErrorScope("validation");

  // (a) clear force_fixed to zero (springs uses atomicAdd; repel writes plain stores).
  // Actually repel pass overwrites all 3 axes via atomicStore. The PAD slot is never
  // read so doesn't matter. But if springs pass exists, we MUST clear before springs
  // runs because springs uses atomicAdd. Cleanest is always-clear before pass A.
  enc.clearBuffer(setup.forceFixedBuf);

  // (b) accumulate_repel — writes force_fixed.
  {
    const pass = enc.beginComputePass({ label: "forceLayout-repel" });
    pass.setPipeline(setup.pipelineRepel);
    pass.setBindGroup(0, setup.stepBindGroup);
    pass.dispatchWorkgroups(Math.ceil(n / TILE));
    pass.end();
  }

  // (c) springs scatter — atomicAdd onto force_fixed (after repel finishes; implicit
  // barrier via separate compute pass per WebGPU §21.5).
  if (setup.pipelineSprings && setup.springsBindGroup && nEdges > 0) {
    const pass = enc.beginComputePass({ label: "forceLayout-springs" });
    pass.setPipeline(setup.pipelineSprings);
    pass.setBindGroup(0, setup.springsBindGroup);
    pass.dispatchWorkgroups(Math.ceil(nEdges / SPRING_WG));
    pass.end();
  }

  // (d) integrate — read force_fixed, write vel + pos_out.
  {
    const pass = enc.beginComputePass({ label: "forceLayout-integrate" });
    pass.setPipeline(setup.pipelineIntegrate);
    pass.setBindGroup(0, setup.stepBindGroup);
    pass.dispatchWorkgroups(Math.ceil(n / TILE));
    pass.end();
  }

  // (e) feed pos_out → pos for next frame.
  enc.copyBufferToBuffer(setup.posOutBuf, 0, setup.posBuf, 0, n * NODE_BYTES);

  // (f) first-step diagnostic: copy a sample of pos_out into the readback buffer
  // so we can verify the kernel actually wrote non-zero, non-NaN values.
  if (setup.firstStep) {
    const sampleN = Math.min(n, 32);
    enc.copyBufferToBuffer(setup.posOutBuf, 0, setup.diagReadbackBuf, 0, sampleN * NODE_BYTES);
  }

  if (ownEncoder) {
    device.queue.submit([enc.finish()]);
    const validationError = await device.popErrorScope();
    if (validationError) {
      throw new Error(`WebGPU validation error in forceLayoutStep: ${validationError.message}`);
    }
  }

  // First-step diag readback. Only on the first call; cheap and one-time.
  if (setup.firstStep) {
    setup.firstStep = false;
    const sampleN = Math.min(n, 32);
    await setup.diagReadbackBuf.mapAsync(GPUMapMode.READ);
    const sample = new Float32Array(setup.diagReadbackBuf.getMappedRange().slice(0));
    setup.diagReadbackBuf.unmap();

    let allZero = true;
    let hasNaN = false;
    for (let i = 0; i < sample.length; i++) {
      const v = sample[i] ?? 0;
      if (Number.isNaN(v)) hasNaN = true;
      if (v !== 0) allZero = false;
    }
    if (hasNaN) {
      throw new Error(
        "forceLayoutStep first-step diag: pos_out contains NaN. " +
          "Most likely cause: zero-length displacement vector → inverseSqrt(0) → Inf → NaN downstream. " +
          "Check MIN_D2 and that initial positions are not all-coincident.",
      );
    }
    if (allZero && n > 0) {
      throw new Error(
        "forceLayoutStep first-step diag: pos_out is all zero. " +
          "Most likely cause: initial pos was zero AND velocity stayed zero (no forces). " +
          "Check that initialPositions were uploaded correctly.",
      );
    }
  }
}

export function disposeForceLayout(setup: ForceLayoutSetup): void {
  setup.paramsBuf.destroy();
  setup.springsParamsBuf.destroy();
  setup.posBuf.destroy();
  setup.posOutBuf.destroy();
  setup.velBuf.destroy();
  setup.forceFixedBuf.destroy();
  if (setup.edgesBuf) setup.edgesBuf.destroy();
  setup.diagReadbackBuf.destroy();
}

/**
 * Diagnostic: read back a sample of pos_out for inspection.
 * Avoid calling per-frame — this stalls the GPU pipeline.
 */
export async function peekPositions(setup: ForceLayoutSetup, sampleN = 32): Promise<Float32Array> {
  const n = Math.min(sampleN, setup.n);
  const tmp = setup.device.createBuffer({
    size: n * NODE_BYTES,
    usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
  });
  const enc = setup.device.createCommandEncoder({ label: "peekPositions" });
  enc.copyBufferToBuffer(setup.posOutBuf, 0, tmp, 0, n * NODE_BYTES);
  setup.device.queue.submit([enc.finish()]);
  await tmp.mapAsync(GPUMapMode.READ);
  const out = new Float32Array(tmp.getMappedRange().slice(0));
  tmp.unmap();
  tmp.destroy();
  return out;
}
